import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { ChevronLeft, ChevronRight, FiberNew, PushPin } from '@mui/icons-material';
import { NewsFeedItem } from '../../../app/services/api/news/newsConsumerTypes';
import { getDefaultNewsImage } from '../../../assets';
import { Section } from '../../../components/ui/Section';
import { NewsModal } from './NewsModal';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface NewsCarouselProps {
  pinnedItems: NewsFeedItem[];
  otherItems: NewsFeedItem[];
  storeNumber?: string;
  loading?: boolean;
  error?: string | null;
}

function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function NewsCard({
  item,
  isPinned,
  onClick,
}: {
  item: NewsFeedItem;
  isPinned?: boolean;
  onClick?: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation('drawer');
  const [imageError, setImageError] = useState(false);
  const imageUrl = item.hero?.url && !imageError
    ? item.hero.url
    : getDefaultNewsImage(Math.abs(item.newsItemId) % 4);

  return (
    <Section
      style={{
        minWidth: isPinned ? 360 : 280,
        maxWidth: isPinned ? 360 : 280,
        height: isPinned ? 340 : 300,
        marginLeft: isPinned ? 0 : 4,
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        borderColor: isPinned ? theme.palette.primary.main : theme.palette.custom.gray[300],
        borderWidth: isPinned ? 3 : 1,
        overflow: 'hidden',
        position: 'relative',
      }}
      onClick={onClick}
    >
      <Box sx={{ height: '50%', position: 'relative' }}>
        <Box
          component="img"
          src={imageUrl}
          alt={item.hero?.altText || item.title}
          onError={() => setImageError(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Stack direction="row"
          spacing={1}
          sx={{ position: 'absolute', top: 8, right: 8 }}>
          {item.readState.isUpdated && (
            <Chip icon={<FiberNew sx={{ fontSize: 14 }} />}
              label={t('newsUpdated').toUpperCase()}
              size="small"
              color="warning" />
          )}
          {!item.readState.isRead && !item.readState.isUpdated && (
            <Chip label={t('newsNew').toUpperCase()}
              size="small"
              color="primary" />
          )}
          {isPinned && (
            <Chip
              icon={<PushPin sx={{ fontSize: 12, transform: 'rotate(45deg)' }} />}
              label={t('newsPinned').toUpperCase()}
              size="small"
              color="primary"
            />
          )}
        </Stack>
      </Box>

      <CardContent sx={{ flex: 1, p: '12px', '&:last-child': { pb: '12px' }, display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb="4px">
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ fontSize: 11 }}>
            {formatDate(item.publishedAtUtc)}
          </Typography>
          {item.tags[0] && <Chip label={item.tags[0].name}
            size="small"
            sx={{ height: 18, fontSize: 10 }} />}
        </Stack>
        <Typography
          variant="textMD"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.3,
            mb: '4px',
          }}
        >
          {item.title}
        </Typography>
        <Typography
          variant="textSM"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            fontSize: 11,
          }}
        >
          {item.summary}
        </Typography>
      </CardContent>
    </Section>
  );
}

export function NewsCarousel({ pinnedItems, otherItems, storeNumber, loading, error }: NewsCarouselProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('drawer');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [selectedItem, setSelectedItem] = useState<NewsFeedItem | null>(null);
  const [readPublicationIds, setReadPublicationIds] = useState<Set<number>>(new Set());

  const withLocalReadState = useCallback((item: NewsFeedItem): NewsFeedItem =>
    readPublicationIds.has(item.publicationId)
      ? { ...item, readState: { ...item.readState, isRead: true, isUpdated: false } }
      : item,
  [readPublicationIds]);

  const displayedPinnedItem = pinnedItems[0] ? withLocalReadState(pinnedItems[0]) : null;
  const carouselItems = [...pinnedItems.slice(1), ...otherItems].map(withLocalReadState);

  const handleRead = useCallback((publicationId: number) => {
    setReadPublicationIds(current => {
      const next = new Set(current);
      next.add(publicationId);
      return next;
    });
    setSelectedItem(current => current && current.publicationId === publicationId
      ? { ...current, readState: { ...current.readState, isRead: true, isUpdated: false } }
      : current);
  }, []);

  const scroll = useCallback((direction: 'left' | 'right') => {
    scrollContainerRef.current?.scrollBy({
      left: direction === 'left' ? -300 : 300,
      behavior: 'smooth',
    });
  }, []);

  if (loading) {
    return (
      <Stack direction="row"
        spacing={2}
        sx={{ overflow: 'hidden' }}>
        {[1, 2, 3].map(i => <Skeleton key={i}
          variant="rounded"
          width={280}
          height={300} />)}
      </Stack>
    );
  }

  // News is supplemental. A failed News request must not block the operational dashboard.
  if (error || (!displayedPinnedItem && carouselItems.length === 0)) return null;

  return (
    <Box sx={{ mb: 3, position: 'relative' }}>
      <Stack direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}>
        <Typography variant="textMD"
          fontWeight={700}>{t('newsTitle')}</Typography>
        <Button variant="secondary"
          size="md"
          onClick={() => navigate('/news')}>{t('newsViewAll')}</Button>
      </Stack>
      <Stack direction="row"
        spacing={2}>
        {displayedPinnedItem && (
          <NewsCard
            item={displayedPinnedItem}
            isPinned
            onClick={() => setSelectedItem(displayedPinnedItem)}
          />
        )}

        {carouselItems.length > 0 && (
          <Box sx={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="small"
              onClick={() => scroll('left')}
              sx={{ position: 'absolute', left: 0, zIndex: 2, bgcolor: theme.palette.background.paper }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>

            <Stack
              direction="row"
              spacing={2}
              ref={scrollContainerRef}
              sx={{
                overflowX: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                mx: 5,
              }}
            >
              {carouselItems.map(item => (
                <NewsCard
                  key={item.publicationId}
                  item={item}
                  onClick={() => setSelectedItem(item)}
                />
              ))}
            </Stack>

            <IconButton
              size="small"
              onClick={() => scroll('right')}
              sx={{ position: 'absolute', right: 0, zIndex: 2, bgcolor: theme.palette.background.paper }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Stack>

      <NewsModal
        open={selectedItem !== null}
        item={selectedItem}
        storeNumber={storeNumber}
        onRead={handleRead}
        onClose={() => setSelectedItem(null)}
      />
    </Box>
  );
}
